import { HttpClient } from '@angular/common/http';
import { StorageService } from './storage.service';
import { MatchmakingData, MatchmakingFormConfig, Steps } from 'matchmaking/models';
import { firstValueFrom } from 'rxjs';
import { Injectable, signal, inject } from '@angular/core';
import { MatchmakingContentService } from './matchmaking-content.service';
import { TrackingMatchmakingSnowplowService } from './tracking-matchmaking-snowplow.service';
import { MatchmakingDataService } from './matchmaking-data.service';

@Injectable({
    providedIn: 'root'
})
export class MatchmakingApiService {
    private http = inject(HttpClient);
    private storageService = inject(StorageService);
    private matchmakingContentService = inject(MatchmakingContentService);
    private matchmakingDataService = inject(MatchmakingDataService);
    private trackingService = inject(TrackingMatchmakingSnowplowService);

    loading = signal(false);
    error = signal<unknown | null>(null);
    private readonly ENDPOINT = 'api/matchmaking/v1/form';


    constructor() {
    }

    async submit() {
        const config = this.matchmakingDataService.config();
        if (!config) return;

        this.loading.set(true);
        try {
            const response = await firstValueFrom(
                this.http.post<MatchmakingFormConfig>('/api/matchmaking/v1/form/submit', config)
            );

            this.trackingService.trackStepComplete(
                { step_number: Number(this.matchmakingContentService.currentStepContent()?.stepCount) },
                this.matchmakingDataService.config()!
            );

            this.matchmakingDataService.updateConfig(response);
            this.storageService.saveData(response);
        } finally {
            this.loading.set(false);
        }
    }

    goToPreviousStep() {
        const config = this.matchmakingDataService.config;

        this.loading.set(true);
        this.http.post<MatchmakingFormConfig>(`${this.ENDPOINT}/back`, config()).subscribe({
            next: (response) => {
                config.set(response);
                this.storageService.saveData(response);
            },
            error: (error) => {
                console.error('Back navigation error details:', error);
                this.error.set(error);
            },
            complete: () => this.loading.set(false)
        });
    }

    async initializeData(initialData: MatchmakingData) {
        const storedData = this.storageService.getData();

        this.matchmakingDataService.setData(initialData);
        this.matchmakingDataService.setConfig(storedData ?? initialData.config);

        if (storedData && storedData.currentStep == Steps.preview) {
            try {
                const response = await firstValueFrom(
                    this.http.post<MatchmakingFormConfig>(`${this.ENDPOINT}/restore`, storedData)
                );

                this.matchmakingDataService.setConfig(response);
                if (response.isCompleted) {
                    this.matchmakingDataService.setFormHasSubmitted();
                }
            } catch (error) {
                this.error.set(error);
            }
        }
    }
}
